import { parseOtpId } from '#/trip-planner/utils/itinerary';
import {
  STREETCAR_ROUTE_IDS,
  FX_BUS_ROUTE_ID,
  MAX_TRAIN_CODES,
  AERIAL_TRAM_SHORT_NAME,
  AERIAL_TRAM_ROUTE_ID,
  BUS_FX_MODE,
  BUS_MODE,
  FX_SHORT_NAME,
  MAX_SHORT_NAME,
  WES_LABEL,
  STREETCAR_MODE,
  WES_TRAIN_ROUTE_ID,
} from '#/shared/constants';
import { getShortNameFromRouteNumber } from '#/shared/utils/route-name-utils';

/**
 * @param routeNumber {number} ID for the route for which an abbreviation will
 * be returned
 * @param summaryFormat {boolean} If `true` select lines that have a color
 * associated with them will return an empty string as their abbreviation
 * @return {string}
 * @note BEWARE! The getShortRouteLabelFrom* functions provide more complete naming, and are probably what you're looking
 * for. This function is passed to OTP in Itinerary, as OTP needs a simpler function for short names. This function
 * will usually give the intended result, but will likely give imperfect output for bus routes like `8a`, which are used
 * internally when schedules are changing soon.
 */
export const imperfectRouteAbbreviator = (
  routeNumber,
  summaryFormat = false
) => {
  return getShortNameFromRouteNumber(routeNumber, summaryFormat);
};

/** Return a list of route ids seen in the itinerary (e.g., filter rt vehicles by route) */
export const itineraryToRouteIds = itinerary =>
  itinerary.legs.filter(leg => leg.routeId).map(leg => leg.routeId);

/**
 * @param routeMode {string} route mode
 * @param shortName {string} supplied short name for route, which we want to override for non-bus
 * @param routeNumber {string|number} route number
 * @return {string}
 * @note private function to convert to special 'MAX' 'WES' 'CL' 'A' 'B' strings for transitive-js route labeling
 */
const getShortRouteLabel = (
  agency,
  routeMode,
  shortName,
  routeNumber,
  route
) => {
  let shortLabel;

  if (routeMode === BUS_MODE || routeMode === BUS_FX_MODE) {
    if (agency.id.includes('TRIMET')) {
      shortLabel = shortName;
    } else {
      shortLabel = route.shortName;
    }
  } else {
    shortLabel = getShortNameFromRouteNumber(
      typeof routeNumber === 'number'
        ? routeNumber.toString()
        : parseOtpId(routeNumber, true)
    );
  }

  return shortLabel;
};

/**
 * @param leg {Object} leg object
 * @return {string}
 * @note will convert leg to special 'MAX' 'WES' 'CL' 'A' 'B' strings for transitive-js route labeling
 */
export const getShortRouteLabelFromLeg = leg => {
  return getShortRouteLabel(
    leg.agency,
    leg.mode,
    leg.routeShortName,
    leg.routeId,
    leg.route
  );
};

/**
 *
 * @param {number} routeId - number of TriMet route id
 * @returns {string} - one of the constants representing a trimet service mode
 */
export const trimetRouteTypeFromRouteId = routeId => {
  const stringifiedRouteId = String(routeId);
  let routeType;
  switch (true) {
    case stringifiedRouteId === String(FX_BUS_ROUTE_ID):
      routeType = FX_SHORT_NAME;
      break;
    case MAX_TRAIN_CODES.includes(stringifiedRouteId):
      routeType = MAX_SHORT_NAME;
      break;
    case stringifiedRouteId === String(WES_TRAIN_ROUTE_ID):
      routeType = WES_LABEL;
      break;
    // Formatted this way to satisfy the mismatch between JS and TS files (this one and shared/constants.ts)
    case /** @type {readonly string[]} */ (STREETCAR_ROUTE_IDS).includes(
      stringifiedRouteId
    ):
      routeType = STREETCAR_MODE;
      break;
    case stringifiedRouteId === String(AERIAL_TRAM_ROUTE_ID):
      routeType = AERIAL_TRAM_SHORT_NAME;
      break;
    default:
      routeType = BUS_MODE;
      break;
  }
  return routeType;
};
